import type { ICreditWalletLotView } from "@lexicanon/shared"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Box, Center, Group, Paper, Stack, Text } from "@mantine/core"
import { formatDate, formatInteger } from "../../../../lib/format-date"
import { isExpiringSoon, lotTone, sumForfeitedRecently, toPercent } from "../credits-helpers"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/** One live lot — kind label, validity chip, remaining/total, progress, and an
 *  expiry warning sub-line when the lot lapses soon. */
function LotRow({ lot, now }: { lot: ICreditWalletLotView; now: Date }): React.JSX.Element {
  const { i18n } = useLingui()
  const isPack = lot.kind === "purchase"
  const soon = isExpiringSoon(lot.expiresAt, now)

  return (
    <div className={classes.lot}>
      <Group gap="xs" align="center" wrap="nowrap" className={classes.lotHead}>
        <Text size="sm" fw={700}>
          {isPack ? <Trans>Credit pack</Trans> : <Trans>Monthly grant</Trans>}
        </Text>
        {lot.expiresAt ? (
          <Badge variant="light" color={lotTone(lot.kind)} size="sm">
            {isPack ? (
              <Trans>valid until {formatDate(i18n, lot.expiresAt, { year: false })}</Trans>
            ) : (
              <Trans>renews {formatDate(i18n, lot.expiresAt, { year: false })}</Trans>
            )}
          </Badge>
        ) : null}
      </Group>

      <Text size="sm" ta="right" className={classes.num}>
        <Text span fw={700}>
          {formatInteger(i18n, lot.remainingCredits)}
        </Text>{" "}
        <Text span c="dimmed">
          <Trans>of {formatInteger(i18n, lot.credits)}</Trans>
        </Text>
      </Text>

      <span className={classes.track} aria-hidden>
        <span
          className={classes.fill}
          data-tone={lotTone(lot.kind)}
          style={{ width: `${toPercent(lot.remainingCredits, lot.credits)}%` }}
        />
      </span>

      {soon ? (
        <Text size="xs" className={classes.expiryNote}>
          <Trans>
            {formatInteger(i18n, lot.remainingCredits)} credits expire{" "}
            {formatDate(i18n, lot.expiresAt, { year: false })}
          </Trans>
        </Text>
      ) : null}
    </div>
  )
}

/**
 * "What's in your wallet" — one row per live credit lot (grant vs prepaid pack)
 * with its remaining balance, validity, and an expiry warning when it lapses
 * soon, plus an honest trailing line of credits lost to expiry in the last 90
 * days. Pure presentation; derivations run through the tested helpers. CREDITS
 * ONLY.
 */
export const CreditWalletCard = ({ wallet, enforcementEnabled, now }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const clock = now ?? new Date()

  const liveLots = wallet?.liveLots ?? []
  const forfeited = wallet ? sumForfeitedRecently(wallet.expiredLots, clock) : 0

  return (
    <Paper withBorder radius="md" p="lg" className={classes.card}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="sm">
        <Trans>What's in your wallet</Trans>
      </Text>

      {wallet === null ? (
        <Center py="xl">
          <Text c="dimmed" size="sm">
            —
          </Text>
        </Center>
      ) : liveLots.length === 0 ? (
        <Center py="xl">
          <Stack gap={4} align="center">
            <Text c="dimmed" size="sm">
              <Trans>No credits added yet.</Trans>
            </Text>
            {enforcementEnabled ? null : (
              <Text c="dimmed" size="sm">
                <Trans>During the pilot you don't need any.</Trans>
              </Text>
            )}
          </Stack>
        </Center>
      ) : (
        <Stack gap={0}>
          {liveLots.map((lot) => (
            <LotRow key={lot.id} lot={lot} now={clock} />
          ))}
        </Stack>
      )}

      {forfeited > 0 ? (
        <Box className={classes.forfeit}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">
              <Trans>Lost to expiry in the last 90 days</Trans>
            </Text>
            <Text size="sm" fw={700} c="dimmed" className={classes.num}>
              {formatInteger(i18n, forfeited)}
            </Text>
          </Group>
        </Box>
      ) : null}
    </Paper>
  )
}
